// Reusable Inquiry / Consultation Form — used on landing (top) and
// bottom of every authenticated page. Uses the public endpoint
// POST /api/public/inquiries (no auth required).
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SERVICES = [
  "Healthcare LMS Platform",
  "White-Label Learning Portal",
  "Corporate Healthcare Training",
  "Nursing / Paramedical Curriculum",
  "Compliance & Audit Training",
  "Exam & Certification Engine",
  "Custom Integration",
  "Consultation / Other",
];

export function InquiryForm({ variant = "hero", title, subtitle, sourcePage = "" }) {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", service_interest: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim() || !f.email.trim() || !f.phone.trim()) {
      toast.error("Name, email and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/inquiries", {
        ...f,
        source_page: sourcePage || (typeof window !== "undefined" ? window.location.pathname : ""),
      });
      setDone(true);
      toast.success("Thank you! We'll reach out within 24 hours.");
      setF({ full_name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Styling variants: 'hero' = navy card matching provided mock; 'compact' = tighter card for page bottoms.
  const isCompact = variant === "compact";

  return (
    <div className={`overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm ${isCompact ? "" : "shadow-lg shadow-slate-200/40"}`} data-testid="inquiry-form-card">
      <div className="bg-[#101876] px-6 py-5 md:px-8" data-testid="inquiry-form-header">
        <h3 className={`font-black text-white ${isCompact ? "text-lg md:text-xl" : "text-2xl md:text-3xl"}`} style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          {title || "Schedule a Free Consultation"}
        </h3>
        <p className={`mt-1 text-white/80 ${isCompact ? "text-xs" : "text-sm"}`}>
          {subtitle || "Our experts will guide you through the process"}
        </p>
      </div>
      <form onSubmit={submit} className={`grid gap-4 ${isCompact ? "p-5 md:p-6" : "p-6 md:p-8"} md:grid-cols-2`} data-testid="inquiry-form">
        <FormField label="Full Name" required>
          <input type="text" placeholder="Enter your name" value={f.full_name} onChange={set("full_name")} required data-testid="inquiry-name"
                 className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
        </FormField>
        <FormField label="Email Address" required>
          <input type="email" placeholder="your@email.com" value={f.email} onChange={set("email")} required data-testid="inquiry-email"
                 className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
        </FormField>
        <FormField label="Phone Number" required>
          <input type="tel" placeholder="+91 9876543210" value={f.phone} onChange={set("phone")} required data-testid="inquiry-phone"
                 className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
        </FormField>
        <FormField label="Service Interested In">
          <select value={f.service_interest} onChange={set("service_interest")} data-testid="inquiry-service"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm bg-white focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20">
            <option value="">Select a service</option>
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Message (Optional)">
            <textarea rows={isCompact ? 3 : 5} placeholder="Tell us about your requirements..." value={f.message} onChange={set("message")} data-testid="inquiry-message"
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm resize-y focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
          </FormField>
        </div>
        <div className="md:col-span-2 space-y-2">
          <button type="submit" disabled={busy || done} data-testid="inquiry-submit"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-white transition ${done ? "bg-[#329112]/70 cursor-default" : "bg-[#329112] hover:bg-[#26720d]"} disabled:opacity-70`}>
            {done ? <><CheckCircle2 className="h-4 w-4" /> Consultation Requested</> : busy ? "Submitting..." : <><Send className="h-4 w-4" /> Get Free Consultation</>}
          </button>
          <p className="text-center text-[11px] text-slate-500">
            By submitting, you agree to our privacy policy. We&apos;ll never share your information.
          </p>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-800 mb-1.5">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

// Section wrapper for pages
export function InquirySection({ variant = "compact", background = "gradient", ...props }) {
  const bg = background === "gradient"
    ? "bg-gradient-to-br from-slate-50 via-white to-slate-50"
    : "bg-white";
  return (
    <section className={`border-t border-slate-200 ${bg} py-10 md:py-14`} data-testid="inquiry-section">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <InquiryForm variant={variant} {...props} />
      </div>
    </section>
  );
}
